import { DISHES } from "./dishes";
import { DOSE_AT, MAX_SHOTS, SYRUPS, doseStatus, heatStatus, type Syrup } from "./coffee";
import { DRINKS, DRINK_NAMES, TOPPINGS, TOPPING_NAMES, friesDoneness, glassStatus, needsFlip, pattyDoneness, type Drink, type Topping } from "./food";
import type { ShiftSetup } from "./restaurants";
import { LEDGE_SIZE, MAX_PATTIES, boardBasket, boardSlot, cupPlan, machineStatus, matchingCoffee, matchingTicket, sinkStatus, whyNot, whyNotCoffee, type ShiftAction, type ShiftState } from "./shift";

// Each restaurant's kitchen as a place: the room, its stations, walking
// around it and what you're looking at. Plain math with no three.js, so the
// renderer, the engine and the tests all share one kitchen.
//
// Metres, y up. The back wall is z = 0, the front wall (with the pass or
// pickup window) is z = depth; x runs left to right as you face the back
// wall. A layout lists the stations it has; a station it doesn't have isn't
// there to aim at or draw.

export const EYE = 1.6;
/** How far your hands reach, from your eyes. */
export const REACH = 1.9;
/** Walking speed, m/s. */
export const WALK = 2.6;
/** Turning speed with keys or the stick, rad/s. */
export const TURN = 2.2;
const RADIUS = 0.3;
const MAX_PITCH = 1.3;
export const COUNTER_TOP = 0.92;

export interface Box {
  min: [number, number, number];
  max: [number, number, number];
}

export type Station =
  | "tub"
  | "sink"
  | "rack"
  | "machine"
  | "shelf"
  | "fridge"
  | "griddle"
  | "fryer"
  | "freezer"
  | "board"
  | "window"
  | "trash"
  | `bin:${Topping}`
  | `soda:${Drink}`
  // The coffee truck's.
  | "milk"
  | "group"
  | "wand"
  | "grinder"
  | `pump:${Syrup}`
  | "cups"
  | "bar";

export interface Pose {
  x: number;
  z: number;
  /** Turned left of facing the back wall, radians. */
  yaw: number;
  /** Looking up (+) or down (-), radians. */
  pitch: number;
}

export interface Layout {
  id: ShiftSetup["layout"];
  width: number;
  depth: number;
  height: number;
  /** Where each station is, for aiming at it and drawing it. */
  stations: Partial<Record<Station, Box>>;
  /** What you can't walk through (walls are separate). */
  colliders: Box[];
  /** Where you stand to work each station, facing it: the bots' walking times. */
  stands: Partial<Record<Station, { x: number; z: number }>>;
  /** Clocking in. */
  start: Pose;
  /** The opening in the front wall: the pass or the pickup window. */
  opening: { x0: number; x1: number; y0: number; y1: number };
}

// --- the dish pit (Sunny Side Diner, Lucky Noodle) -------------------------
//
// Along the back wall, left to right, the dirty side flows to the clean: bus
// tub, sink, the rack on its drainboard, the dish machine. The clean shelf
// stands on the right wall by the pass, a walk away, and the swing door to
// the dining room is on the left.

const dishPitShelf: Box = { min: [6.45, 0, 2.3], max: [7, 1.9, 4.5] };

export const DISH_PIT: Layout = {
  id: "dish-pit",
  width: 7,
  depth: 5,
  height: 3,
  stations: {
    tub: { min: [0.75, 0.9, 0.1], max: [1.75, 1.15, 0.68] },
    sink: { min: [1.85, 0.62, 0.1], max: [2.85, 0.98, 0.7] },
    rack: { min: [3.02, 0.92, 0.1], max: [3.86, 1.1, 0.7] },
    machine: { min: [3.92, 0, 0.04], max: [4.78, 2, 0.8] },
    shelf: dishPitShelf,
  },
  colliders: [
    { min: [0.6, 0, 0], max: [5.3, 1, 0.76] }, // the dish line, tub counter to clean table
    dishPitShelf,
    { min: [0.05, 0, 0.15], max: [0.55, 0.8, 0.65] }, // trash can by the tub
    { min: [0, 0, 4.25], max: [0.7, 0.6, 5] }, // mop bucket corner
  ],
  stands: {
    tub: { x: 1.6, z: 1.2 },
    sink: { x: 2.2, z: 1.2 },
    rack: { x: 3.45, z: 1.15 },
    machine: { x: 4.35, z: 1.2 },
    shelf: { x: 5.95, z: 3.4 },
  },
  start: { x: 2.35, z: 1.35, yaw: 0, pitch: -0.65 },
  opening: { x0: 4.2, x1: 6.2, y0: 1.0, y1: 1.85 },
};

// --- the Burger Shack --------------------------------------------------------
//
// A small shack. The cooking line runs along the back wall, left to right:
// the fridge (raw patties), the flat-top, the fryer, the freezer (frozen
// fries), and the trash. The front wall has, left to right, the shelf of clean
// baskets, the assembly board with its topping bins along the wall, the
// pickup window, and the bus tub where customers drop their baskets. The dish
// pit runs up the right wall from the front: sink, drainboard rack, machine.

// The bins run in `TOPPINGS` order from your left as you face the board
// (the front wall), so from high x to low.
const SHACK_BINS_X1 = 2.94;
const BIN_WIDTH = 0.22;
// The soda machine stands against the left wall; its nozzles run in `DRINKS`
// order from your left as you face it (from high z to low).
const SODA_Z1 = 2.82;
const NOZZLE_WIDTH = 0.26;
const nozzles = Object.fromEntries(
  DRINKS.map((d, i) => [`soda:${d}`, { min: [0.15, 1.0, SODA_Z1 - (i + 1) * NOZZLE_WIDTH], max: [0.52, 1.42, SODA_Z1 - i * NOZZLE_WIDTH] } satisfies Box]),
) as Record<`soda:${Drink}`, Box>;

const bins = Object.fromEntries(
  TOPPINGS.map((t, i) => [`bin:${t}`, { min: [SHACK_BINS_X1 - (i + 1) * BIN_WIDTH, 0.92, 4.08], max: [SHACK_BINS_X1 - i * BIN_WIDTH, 1.06, 4.34] } satisfies Box]),
) as Record<`bin:${Topping}`, Box>;

export const BURGER_SHACK: Layout = {
  id: "burger-shack",
  width: 6,
  depth: 4.4,
  height: 2.8,
  stations: {
    fridge: { min: [0.08, 0, 0.05], max: [0.8, 1.85, 0.75] },
    griddle: { min: [0.95, 0.86, 0.1], max: [2.15, 1.0, 0.7] },
    fryer: { min: [2.3, 0.8, 0.1], max: [2.96, 1.0, 0.7] },
    freezer: { min: [3.08, 0, 0.06], max: [3.78, 0.92, 0.74] },
    trash: { min: [3.95, 0, 0.12], max: [4.4, 0.85, 0.57] },
    shelf: { min: [0.08, 0, 3.75], max: [1.4, 1.5, 4.36] },
    board: { min: [1.6, 0.9, 3.68], max: [2.96, 1.0, 4.08] },
    ...bins,
    ...nozzles,
    window: { min: [3.15, 0.95, 4.12], max: [4.55, 1.9, 4.4] },
    tub: { min: [4.72, 0.92, 3.8], max: [5.55, 1.15, 4.34] },
    sink: { min: [5.25, 0.62, 2.55], max: [5.95, 0.98, 3.4] },
    rack: { min: [5.25, 0.92, 1.62], max: [5.95, 1.1, 2.4] },
    machine: { min: [5.15, 0, 0.62], max: [5.98, 2, 1.5] },
  },
  colliders: [
    { min: [0, 0, 0], max: [4.45, 1, 0.76] }, // the cooking line and the trash
    { min: [0, 0, 3.66], max: [3.05, 1, 4.4] }, // basket shelf and the assembly board
    { min: [3.05, 0, 4.1], max: [4.6, 1, 4.4] }, // the window's ledge
    { min: [4.6, 0, 3.75], max: [6, 1, 4.4] }, // the bus tub's counter
    { min: [5.12, 0, 0.55], max: [6, 1, 3.45] }, // the dish pit along the right wall
    { min: [0, 0, 1.72], max: [0.62, 1, 2.88] }, // the soda machine's counter
  ],
  stands: {
    fridge: { x: 0.5, z: 1.2 },
    griddle: { x: 1.55, z: 1.2 },
    fryer: { x: 2.62, z: 1.2 },
    freezer: { x: 3.4, z: 1.2 },
    trash: { x: 4.15, z: 1.15 },
    shelf: { x: 0.75, z: 3.2 },
    board: { x: 2.3, z: 3.2 },
    ...Object.fromEntries(TOPPINGS.map((t) => [`bin:${t}`, { x: 2.3, z: 3.2 }])),
    ...Object.fromEntries(DRINKS.map((d) => [`soda:${d}`, { x: 1.1, z: 2.3 }])),
    window: { x: 3.85, z: 3.6 },
    tub: { x: 4.8, z: 3.3 },
    sink: { x: 4.7, z: 2.95 },
    rack: { x: 4.7, z: 2.0 },
    machine: { x: 4.65, z: 1.1 },
  },
  start: { x: 1.9, z: 1.6, yaw: 0, pitch: -0.55 },
  opening: { x0: 3.15, x1: 4.55, y0: 1.0, y1: 1.9 },
};

// --- the coffee truck (The Daily Grind) ---------------------------------------
//
// A narrow galley: the back counter runs, left to right, the milk fridge
// under the counter, the espresso machine's two group heads and its steam
// wand, the grinder, the three syrup pumps, the hand sink, the bin of dirty
// glassware and the shelf of clean. Across the aisle, the front counter: the
// paper cup stack, the bar where each coffee's made, the service window onto
// the food truck lot, and the trash.

const BACK = 0.68;
const FRONT = 1.83;
const pumps = Object.fromEntries(
  SYRUPS.map((sy, i) => [`pump:${sy}`, { min: [2.55 + i * 0.22, 0.92, 0.1], max: [2.75 + i * 0.22, 1.28, 0.42] } satisfies Box]),
) as Record<`pump:${Syrup}`, Box>;
const truckShelf: Box = { min: [4.7, 0, 0.05], max: [5.15, 1.75, 0.5] };

export const COFFEE_TRUCK: Layout = {
  id: "coffee-truck",
  width: 5.2,
  depth: 2.5,
  height: 2.3,
  stations: {
    milk: { min: [0.1, 0, 0.1], max: [0.72, 0.88, 0.66] },
    group: { min: [0.82, 0.95, 0.1], max: [1.68, 1.32, 0.55] },
    wand: { min: [1.72, 0.95, 0.12], max: [1.96, 1.4, 0.55] },
    grinder: { min: [2.04, 0.92, 0.1], max: [2.42, 1.5, 0.5] },
    ...pumps,
    sink: { min: [3.35, 0.62, 0.08], max: [4.0, 0.95, 0.66] },
    tub: { min: [4.08, 0.92, 0.1], max: [4.6, 1.1, 0.55] },
    shelf: truckShelf,
    cups: { min: [0.1, 0.92, 1.9], max: [0.46, 1.3, 2.4] },
    bar: { min: [0.55, 0.9, 1.9], max: [1.55, 1.0, 2.4] },
    window: { min: [1.7, 0.95, 2.3], max: [3.5, 1.9, 2.5] },
    trash: { min: [4.6, 0, 1.95], max: [5.1, 0.85, 2.4] },
  },
  colliders: [
    { min: [0, 0, 0], max: [5.2, 1, BACK] }, // the back counter
    { min: [0, 0, FRONT], max: [5.2, 1, 2.5] }, // the front counter, the window ledge and the trash
  ],
  stands: {
    milk: { x: 0.45, z: 1.25 },
    group: { x: 1.25, z: 1.25 },
    wand: { x: 1.84, z: 1.25 },
    grinder: { x: 2.23, z: 1.25 },
    ...Object.fromEntries(SYRUPS.map((sy) => [`pump:${sy}`, { x: 2.87, z: 1.25 }])),
    sink: { x: 3.68, z: 1.25 },
    tub: { x: 4.34, z: 1.25 },
    shelf: { x: 4.9, z: 1.25 },
    cups: { x: 0.4, z: 1.25 },
    bar: { x: 1.05, z: 1.25 },
    window: { x: 2.6, z: 1.45 },
    trash: { x: 4.85, z: 1.3 },
  },
  start: { x: 2.4, z: 1.25, yaw: 0, pitch: -0.5 },
  opening: { x0: 1.7, x1: 3.5, y0: 1.0, y1: 1.9 },
};

export const LAYOUTS: Record<Layout["id"], Layout> = { "dish-pit": DISH_PIT, "burger-shack": BURGER_SHACK, "coffee-truck": COFFEE_TRUCK };

export function layoutFor(setup: ShiftSetup): Layout {
  return LAYOUTS[setup.layout];
}

/** A frame's movement: walk and strafe in [-1, 1], turn in [-1, 1] (left +), plus look deltas in radians. */
export interface MoveInput {
  forward: number;
  strafe: number;
  turn: number;
  lookYaw: number;
  lookPitch: number;
}

export const NO_MOVE: MoveInput = { forward: 0, strafe: 0, turn: 0, lookYaw: 0, lookPitch: 0 };

/** Which way you're looking, as a unit vector. */
export function lookDir(p: Pose): [number, number, number] {
  const c = Math.cos(p.pitch);
  return [-Math.sin(p.yaw) * c, Math.sin(p.pitch), -Math.cos(p.yaw) * c];
}

export function movePlayer(layout: Layout, p: Pose, input: MoveInput, dt: number): Pose {
  const yaw = p.yaw + input.lookYaw + input.turn * TURN * dt;
  const pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, p.pitch + input.lookPitch));
  let fwd = input.forward;
  let side = input.strafe;
  const len = Math.hypot(fwd, side);
  if (len > 1) {
    fwd /= len;
    side /= len;
  }
  const step = WALK * dt;
  // Forward is where you face on the floor; strafing right is a quarter turn clockwise of it.
  const fx = -Math.sin(yaw);
  const fz = -Math.cos(yaw);
  let x = p.x + (fx * fwd - fz * side) * step;
  let z = p.z + (fz * fwd + fx * side) * step;
  for (const box of layout.colliders) [x, z] = pushOut(x, z, box);
  x = Math.max(RADIUS, Math.min(layout.width - RADIUS, x));
  z = Math.max(RADIUS, Math.min(layout.depth - RADIUS, z));
  return { x, z, yaw, pitch };
}

/** Moves a player circle at (x, z) out of a box's footprint. */
function pushOut(x: number, z: number, b: Box): [number, number] {
  const cx = Math.max(b.min[0], Math.min(b.max[0], x));
  const cz = Math.max(b.min[2], Math.min(b.max[2], z));
  const dx = x - cx;
  const dz = z - cz;
  const d = Math.hypot(dx, dz);
  if (d >= RADIUS) return [x, z];
  if (d > 1e-6) return [cx + (dx / d) * RADIUS, cz + (dz / d) * RADIUS];
  // Inside the box: out by the nearest side.
  const out = [
    [b.min[0] - RADIUS - x, 0],
    [b.max[0] + RADIUS - x, 0],
    [0, b.min[2] - RADIUS - z],
    [0, b.max[2] + RADIUS - z],
  ].sort((a, c) => Math.hypot(a[0]!, a[1]!) - Math.hypot(c[0]!, c[1]!))[0]!;
  return [x + out[0]!, z + out[1]!];
}

/** Where the `i`th dish in the tub sits (its centre): rows of four across the tub, stacking up. */
export function tubSlot(layout: Layout, i: number): [number, number, number] {
  const tub = layout.stations.tub!;
  const layer = Math.floor(i / 12);
  const j = i % 12;
  const col = j % 4;
  const row = Math.floor(j / 4);
  const w = tub.max[0] - tub.min[0];
  const d = tub.max[2] - tub.min[2];
  return [tub.min[0] + w * (0.15 + col * 0.233), tub.min[1] + 0.1 + layer * 0.07, tub.min[2] + d * (0.21 + row * 0.31)];
}

/** The centre of the `i`th of `n` spots along a station (a flat-top's, a fryer's baskets). */
export function slotCentre(box: Box, i: number, n: number): [number, number, number] {
  const w = (box.max[0] - box.min[0]) / n;
  return [box.min[0] + w * (i + 0.5), box.max[1], (box.min[2] + box.max[2]) / 2];
}

const DISH_RADIUS = 0.12;

/** What's under the crosshair and within reach: a station, and which spot of it, or which dish in the tub. */
export type Aim = { station: Station; slot?: number; dishId?: number; distance: number } | null;

export function aimAt(layout: Layout, p: Pose, s: ShiftState): Aim {
  const eye: [number, number, number] = [p.x, EYE, p.z];
  const dir = lookDir(p);
  let best: Aim = null;
  for (const [station, box] of Object.entries(layout.stations) as [Station, Box][]) {
    const t = rayBox(eye, dir, box);
    if (t !== null && t <= REACH && (!best || t < best.distance)) best = { station, distance: t };
  }
  // Which spot along the flat-top, fryer or board: by where the ray hits it.
  if (best && (best.station === "griddle" || best.station === "fryer" || best.station === "group" || best.station === "board")) {
    const box = layout.stations[best.station]!;
    const spots = { griddle: s.griddle, fryer: s.fryer, group: s.groups, board: s.board }[best.station];
    const n = Math.max(1, spots.length);
    const hitX = eye[0] + dir[0] * best.distance;
    best.slot = Math.max(0, Math.min(n - 1, Math.floor(((hitX - box.min[0]) / (box.max[0] - box.min[0])) * n)));
  }
  // The dish you're pointing at in the tub (dishes stick up out of its box).
  if (layout.stations.tub) {
    let dish: Aim = null;
    for (let i = 0; i < s.tub.length; i++) {
      const t = raySphere(eye, dir, tubSlot(layout, i), DISH_RADIUS);
      if (t !== null && t <= REACH && (!dish || t < dish.distance)) dish = { station: "tub", dishId: s.tub[i]!.id, distance: t };
    }
    if (dish && (!best || best.station === "tub" || dish.distance <= best.distance)) return dish;
  }
  return best;
}

function rayBox(o: [number, number, number], d: [number, number, number], b: Box): number | null {
  let near = -Infinity;
  let far = Infinity;
  for (let a = 0; a < 3; a++) {
    if (Math.abs(d[a]!) < 1e-9) {
      if (o[a]! < b.min[a]! || o[a]! > b.max[a]!) return null;
      continue;
    }
    const t1 = (b.min[a]! - o[a]!) / d[a]!;
    const t2 = (b.max[a]! - o[a]!) / d[a]!;
    near = Math.max(near, Math.min(t1, t2));
    far = Math.min(far, Math.max(t1, t2));
  }
  if (near > far || far < 0) return null;
  return Math.max(near, 0);
}

function raySphere(o: [number, number, number], d: [number, number, number], c: [number, number, number], r: number): number | null {
  const oc = [o[0] - c[0], o[1] - c[1], o[2] - c[2]];
  const b = oc[0]! * d[0] + oc[1]! * d[1] + oc[2]! * d[2];
  const cc = oc[0]! ** 2 + oc[1]! ** 2 + oc[2]! ** 2 - r * r;
  const disc = b * b - cc;
  if (disc < 0) return null;
  const t = -b - Math.sqrt(disc);
  return t >= 0 ? t : null;
}

/** What pressing Use does now: a label for the prompt, and the action (null when there's nothing to do). */
export interface Hint {
  label: string;
  action: ShiftAction | null;
}

const lower = (k: keyof typeof DISHES) => DISHES[k].name.toLowerCase();
const say = (label: string, action: ShiftAction | null = null): Hint => ({ label, action });

/** Hands full: where what you're holding can go, so you're never stuck with it. */
function full(s: ShiftState): Hint {
  return say(`Your hands are full: ${whereItGoes(s)}`);
}

function whereItGoes(s: ShiftState): string {
  const h = s.held;
  const cafe = s.setup.service === "cafe";
  const bin = cafe ? "bin" : "tub";
  switch (h?.kind) {
    case "dish":
      return h.dish.grime ? `the ${lower(h.dish.kind)} goes in the ${bin} or the sink` : `the ${lower(h.dish.kind)} goes on the shelf`;
    case "rack":
      return "unload it onto the shelf";
    case "patty":
      return pattyDoneness(h.patty) === "done" ? "it goes in a basket, or the trash" : "it goes on the flat-top, or the trash";
    case "fries":
      return friesDoneness(h.fries) === "done" ? "they go in a basket, or the trash" : "they go in the fryer, or the trash";
    case "basket":
      return "set it on the board, hand it out, or put it back on the shelf";
    case "cup":
      return h.cup.drink ? "take it to the window, or tip it out in the trash" : "fill it at the soda machine, or put it back";
    case "portafilter":
      return "lock it into the espresso machine, or knock it out in the trash";
    case "shot":
      return "pour it into a cup at the bar, or dump it in the trash";
    case "pitcher":
      return "steam it at the wand, pour it at the bar, or dump it in the trash";
    case "coffee":
      return "set it on the bar, hand it out at the window, or throw it out";
    default:
      return "put it down first";
  }
}

/** What's in your hands, in a few words, for "take the ... to the ..." prompts. */
function heldName(s: ShiftState): string {
  const h = s.held;
  if (!h) return "";
  switch (h.kind) {
    case "dish":
      return `${h.dish.grime ? "dirty" : "clean"} ${lower(h.dish.kind)}`;
    case "rack":
      return "clean rack";
    case "patty":
      return "patty";
    case "fries":
      return "fries";
    case "basket":
      return "basket";
    case "cup":
      return h.cup.drink ? DRINK_NAMES[h.cup.drink] : "cup";
    case "portafilter":
      return "portafilter";
    case "shot":
      return "shot";
    case "pitcher":
      return "milk";
    case "coffee":
      return "coffee";
  }
}

export function hintFor(aim: Aim, s: ShiftState): Hint | null {
  if (!aim) return null;
  const held = s.held;
  const window = s.setup.service === "window";
  const station = aim.station;
  if (station.startsWith("bin:")) {
    const topping = station.slice(4) as Topping;
    const name = TOPPING_NAMES[topping];
    // Onto the basket in your hands, or the one you last worked on at the board.
    const basket = held?.kind === "basket" ? held.basket : boardBasket(s);
    if (!basket) return say(`${name[0]!.toUpperCase()}${name.slice(1)}: set a basket on the board first`);
    const which = held?.kind !== "basket" && s.board.filter(Boolean).length > 1 ? ` (${boardSide(boardSlot(s))} basket)` : "";
    return basket.contents.toppings.includes(topping) ? say(`Take the ${name} off${which}`, { type: "topping", topping }) : say(`Add ${name}${which}`, { type: "topping", topping });
  }
  if (station.startsWith("soda:")) {
    const drink = station.slice(5) as Drink;
    const name = DRINK_NAMES[drink];
    if (!held) return s.shelf.tumbler ? say(`Grab a cup (${s.shelf.tumbler} left)`, { type: "takeCup" }) : say("Out of clean cups! Wash some");
    if (held.kind !== "cup") return full(s);
    const cup = held.cup;
    if (cup.drink && cup.drink !== drink && cup.fill > 0) return say(`That cup's got ${DRINK_NAMES[cup.drink]} in it`);
    const status = glassStatus(cup);
    if (status === "spilled") return say(`Spilling over! Tip it out`);
    if (status === "full") return say(`Hold to top up the ${name} (it's full)`, { type: "pour", drink });
    return say(`Hold to pour ${name}`, { type: "pour", drink });
  }
  if (s.setup.service === "cafe") {
    const cafe = cafeHint(station, aim.slot ?? 0, s);
    if (cafe !== undefined) return cafe;
  }
  switch (station) {
    case "tub": {
      if (held?.kind === "dish" && held.dish.grime === 0) return say(`Take the clean ${lower(held.dish.kind)} to the shelf`);
      if (held?.kind === "dish") {
        return say(`Put the ${lower(held.dish.kind)} ${s.setup.service === "cafe" ? "in the bin" : "back"}`, { type: "putBack" });
      }
      if (held) return full(s);
      if (!s.tub.length) return say("The bus tub's empty");
      const dish = s.tub.find((d) => d.id === aim.dishId) ?? s.tub[0]!;
      return say(`Pick up the ${lower(dish.kind)}`, { type: "take", dishId: dish.id });
    }
    case "sink": {
      if (held?.kind === "cup" && held.cup.drink) return say("Tip out the drink (the cup goes to the wash)", { type: "trash" });
      const status = sinkStatus(s);
      if (held?.kind === "dish" && held.dish.grime === 0) return say(`Take the clean ${lower(held.dish.kind)} to the shelf`);
      if (held?.kind === "dish") return status === "empty" ? say(`Drop the ${lower(held.dish.kind)} in the sink`, { type: "toSink" }) : say("The sink's busy");
      if (held) return full(s);
      if (status === "dirty") {
        const hand = DISHES[s.sink!.kind].handWash ? ", hand wash only" : "";
        return say(`Hold to scrub the ${lower(s.sink!.kind)} (${s.sink!.grime} left${hand})`, { type: "scrub" });
      }
      if (status === "rinsed") return say(`Lift out the clean ${lower(s.sink!.kind)}`, { type: "lift" });
      if (status === "blocked") return say("The rack's full. Run the machine");
      return say("Bring a dirty dish from the tub");
    }
    case "rack":
    case "machine": {
      const status = machineStatus(s);
      if (status === "washing") return say(`Washing… ${Math.ceil(s.machine!.doneAt - s.t)}s`);
      if (status === "done") return held ? say("Free your hands to take the clean rack") : say("Take out the clean rack", { type: "machine" });
      if (held?.kind === "rack") return say("Take the rack to the shelf");
      if (held?.kind === "dish" && held.dish.grime === 0) return say(`Take the clean ${lower(held.dish.kind)} to the shelf`);
      if (!s.rack.length) return say("The rack's empty");
      return say(`Run the machine (${s.rack.length} in the rack)`, { type: "machine" });
    }
    case "shelf": {
      if (held?.kind === "dish" && held.dish.grime === 0) return say(`Put the clean ${lower(held.dish.kind)} on the shelf`, { type: "shelve" });
      if (held?.kind === "rack") return say(`Put away ${held.dishes.length} clean ${held.dishes.length === 1 ? "dish" : "dishes"}`, { type: "shelve" });
      if (!window) return say("The clean shelf");
      if (held?.kind === "cup") return held.cup.drink ? say("Only empty cups go back on the shelf") : say("Put the cup back", { type: "shelve" });
      if (held?.kind === "basket") {
        const c = held.basket.contents;
        return c.patties.length || c.toppings.length || c.fries ? say("Only empty baskets go back on the shelf") : say("Put the basket back", { type: "shelve" });
      }
      if (held && !doneFood(held)) return full(s);
      if (!s.shelf.basket) return say("Out of clean baskets! Wash some");
      if (held) return say(`Put the ${held.kind === "patty" ? "patty" : "fries"} in a clean basket`, { type: "takeBasket" });
      return say(`Grab a clean basket (${s.shelf.basket} left)`, { type: "takeBasket" });
    }
    case "fridge":
      return held ? full(s) : say("Take a raw patty", { type: "takePatty" });
    case "freezer":
      return held ? full(s) : say("Take a portion of fries", { type: "takeFries" });
    case "griddle": {
      const slot = aim.slot ?? 0;
      const patty = s.griddle[slot];
      if (!patty) {
        if (held?.kind === "patty" && pattyDoneness(held.patty) !== "burnt") return say("Lay the patty on the flat-top", { type: "griddle", slot });
        if (held) return say(`The flat-top's for patties`);
        return say("Bring a raw patty from the fridge");
      }
      const doneness = pattyDoneness(patty);
      if (held?.kind === "basket" && doneness === "done") {
        return held.basket.contents.patties.length < MAX_PATTIES ? say("Put the patty in the basket", { type: "griddle", slot }) : say("Two patties is the most");
      }
      if (held) return full(s);
      if (doneness === "burnt") return say("Scrape off the burnt patty", { type: "griddle", slot });
      if (doneness === "done") return say("Take the patty off", { type: "griddle", slot });
      if (needsFlip(patty)) return say("Flip it!", { type: "griddle", slot });
      return say(patty.sides[1] >= 1 ? "Cooking the other side…" : "Cooking… flip it when the bottom's browned");
    }
    case "fryer": {
      const slot = aim.slot ?? 0;
      const fries = s.fryer[slot];
      if (!fries) {
        if (held?.kind === "fries" && held.fries.cook === 0) return say("Drop the fries in", { type: "fryer", slot });
        if (held) return say("The fryer's for frozen fries");
        return say("Bring fries from the freezer");
      }
      const doneness = friesDoneness(fries);
      if (held?.kind === "basket" && doneness === "done") {
        return held.basket.contents.fries ? say("It already has fries") : say("Put the fries in the basket", { type: "fryer", slot });
      }
      if (held) return full(s);
      if (doneness === "burnt") return say("Lift out the burnt fries", { type: "fryer", slot });
      return say(doneness === "done" ? "Lift the fries out" : "Lift the fries out (not done!)", { type: "fryer", slot });
    }
    case "board": {
      const slot = aim.slot ?? 0;
      const board = s.board[slot];
      const act = { type: "board", slot } as const;
      if (!board) {
        if (held?.kind === "basket") return say("Set the basket down", act);
        if (held && doneFood(held)) {
          const food = held.kind === "patty" ? "patty" : "fries";
          return s.shelf.basket ? say(`Put the ${food} in a clean basket`, act) : say("Out of clean baskets! Wash some");
        }
        if (held) return say("Build orders here: grab a clean basket from the shelf");
        return say("Grab a clean basket from the shelf");
      }
      if (!held) return say("Pick up the basket", act);
      if (held.kind === "patty") {
        if (board.contents.patties.length >= MAX_PATTIES) return say("Two patties is the most");
        const doneness = pattyDoneness(held.patty);
        const warn = doneness === "done" ? "" : doneness === "burnt" ? " (it's burnt!)" : " (it's not cooked!)";
        return say(`Put the patty on a bun${warn}`, act);
      }
      if (held.kind === "fries") return board.contents.fries ? say("It already has fries") : say("Add the fries", act);
      if (held.kind === "basket") return say("Swap baskets", act);
      return say("There's already a basket here");
    }
    case "window": {
      if (held?.kind === "cup") {
        const plan = cupPlan(s, held.cup);
        if (plan.ticket) return say(`Order up! Hand out the ${plan.ticket.item.toLowerCase()}`, { type: "deliver" });
        if (plan.ledge) return say(`Set the ${DRINK_NAMES[held.cup.drink!]} on the ledge (${s.ledge.length}/${LEDGE_SIZE})`, { type: "deliver" });
        return say(plan.why!);
      }
      if (!held && s.ledge.length) return say(`Take a drink off the ledge`, { type: "takeDrink" });
      if (held?.kind !== "basket") return say(s.tickets.length ? `Pickup window: ${s.tickets.length} waiting` : "Pickup window");
      const why = whyNot(s, held.basket.contents);
      if (why) return say(why);
      const ticket = matchingTicket(s, held.basket.contents)!;
      return say(`Order up! Hand out the ${ticket.item.toLowerCase()}`, { type: "deliver" });
    }
    case "trash": {
      if (held?.kind === "cup") return held.cup.drink ? say("Tip out the drink (the cup goes to the wash)", { type: "trash" }) : say("Don't throw out the cup");
      if (held?.kind === "patty") return say("Throw out the patty", { type: "trash" });
      if (held?.kind === "fries") return say("Throw out the fries", { type: "trash" });
      if (held?.kind === "basket" && (held.basket.contents.patties.length || held.basket.contents.toppings.length || held.basket.contents.fries)) {
        return say("Dump the basket and start over", { type: "trash" });
      }
      return say(held ? `Don't throw out the ${heldName(s)}` : "Trash");
    }
    default:
      return null;
  }
}

/** A board spot as you face it: the first is on your right (low x, facing the front wall). */
export function boardSide(slot: number): "left" | "right" {
  return slot === 0 ? "right" : "left";
}

/** What Use does at the coffee truck's own stations, the window and the trash; undefined for the shared dish pit ones. */
function cafeHint(station: Station, slot: number, s: ShiftState): Hint | null | undefined {
  const held = s.held;
  if (station.startsWith("pump:")) {
    const syrup = station.slice(5) as Syrup;
    if (!s.bar) return say(`${syrup[0]!.toUpperCase()}${syrup.slice(1)} syrup: set a cup on the bar first`);
    return s.bar.syrups.includes(syrup) ? say(`Take the ${syrup} back out`, { type: "pump", syrup }) : say(`Pump of ${syrup}`, { type: "pump", syrup });
  }
  switch (station) {
    case "grinder": {
      if (held && held.kind !== "portafilter") return full(s);
      const dose = held?.kind === "portafilter" ? held.dose : 0;
      const status = doseStatus(dose);
      if (status === "spilled") return say("Overflowing! Knock it out in the trash");
      if (status === "right") return say("Dosed! Lock it into the espresso machine");
      return say(dose > 0 ? "Hold to grind (keep going)" : "Hold to grind a dose", { type: "grind" });
    }
    case "group": {
      const shot = s.groups[slot];
      if (!shot) {
        if (held?.kind !== "portafilter") return say(held ? "The machine takes a dosed portafilter" : "Grind a dose at the grinder first");
        const status = doseStatus(held.dose);
        if (status === "spilled") return say("Overflowing! Knock it out in the trash");
        if (status !== "right") return say(`Not enough coffee: grind to at least ${Math.round(DOSE_AT * 100)}%`);
        return s.shelf.shotglass ? say("Lock it in and pull a shot", { type: "group", slot }) : say("No clean shot glasses! Wash some");
      }
      if (held) return full(s);
      const st = heatStatus(shot).status;
      if (st === "burnt") return say("Bitter! Take the shot and dump it", { type: "group", slot });
      return say(st === "cooking" ? "Stop the shot (it's not done: sour!)" : "Stop the shot", { type: "group", slot });
    }
    case "milk":
      if (held) return full(s);
      return s.shelf.pitcher ? say(`Take a pitcher of milk (${s.shelf.pitcher} left)`, { type: "takeMilk" }) : say("No clean pitchers! Wash some");
    case "wand": {
      if (s.wand) {
        if (held) return full(s);
        const st = heatStatus(s.wand).status;
        if (st === "burnt") return say("Scalded! Take it off and dump it", { type: "wand" });
        return say(st === "cooking" ? "Steaming… (take it off early and it's cold)" : "Take the milk off", { type: "wand" });
      }
      if (held?.kind === "pitcher") return heatStatus(held.milk).status === "burnt" ? say("That milk's scalded: dump it") : say("Steam the milk", { type: "wand" });
      return say(held ? "The wand's for milk" : "Bring a pitcher of milk from the fridge");
    }
    case "cups":
      return held ? full(s) : say("Grab a paper cup", { type: "takeCoffeeCup" });
    case "bar": {
      const bar = s.bar;
      if (!held) return bar ? say("Pick up the coffee", { type: "bar" }) : say("Set a paper cup here to make a coffee");
      if (held.kind === "coffee") return say(bar ? "Swap cups" : "Set the cup down", { type: "bar" });
      if (held.kind === "shot") {
        if (bar && bar.shots.length >= MAX_SHOTS) return say("Two shots is the most");
        const st = heatStatus(held.shot).status;
        const warn = st === "cooking" ? " (it's sour: pulled short!)" : st === "burnt" ? " (it's bitter!)" : "";
        return say(`Pour the shot in${bar ? "" : "to a fresh cup"}${warn}`, { type: "bar" });
      }
      if (held.kind === "pitcher") {
        if (bar?.milk) return say("It already has milk");
        const st = heatStatus(held.milk).status;
        const warn = st === "cooking" ? " (it's cold!)" : st === "burnt" ? " (it's scalded!)" : "";
        return say(`Pour the milk in${bar ? "" : "to a fresh cup"}${warn}`, { type: "bar" });
      }
      return full(s);
    }
    case "window": {
      if (held?.kind !== "coffee") return say(s.tickets.length ? `Service window: ${s.tickets.length} waiting` : "Service window");
      const why = whyNotCoffee(s, held.cup);
      if (why) return say(why);
      const t = matchingCoffee(s, held.cup)!;
      return say(`Call out ${t.name}'s ${t.item.toLowerCase()}!`, { type: "deliver" });
    }
    case "trash":
      if (held?.kind === "portafilter") return say("Knock out the grounds", { type: "trash" });
      if (held?.kind === "coffee") return say("Throw out the coffee", { type: "trash" });
      if (held?.kind === "shot") return say("Dump the shot", { type: "trash" });
      if (held?.kind === "pitcher") return say("Dump the milk", { type: "trash" });
      return undefined;
    case "sink":
      if (held?.kind === "shot") return say("Dump the shot", { type: "trash" });
      if (held?.kind === "pitcher") return say("Dump the milk", { type: "trash" });
      return undefined;
    default:
      return undefined;
  }
}

/** Cooked patty or fries in your hands: ready to go in a basket. */
function doneFood(held: NonNullable<ShiftState["held"]>): boolean {
  return (held.kind === "patty" && pattyDoneness(held.patty) === "done") || (held.kind === "fries" && friesDoneness(held.fries) === "done");
}

/** Seconds to walk between two stations' standing spots (with a moment to turn). */
export function walkTime(layout: Layout, a: Station, b: Station): number {
  const pa = layout.stands[a];
  const pb = layout.stands[b];
  if (a === b || !pa || !pb) return 0;
  const d = Math.hypot(pa.x - pb.x, pa.z - pb.z);
  return d < 0.01 ? 0 : d / WALK + 0.35;
}
